// src/store/slices/administrators/administratorsFormSlice.ts

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AdminEntity, AdminFormState, AdminUpsertDTO } from '@/types/administrators.types';
import { createAdminThunk } from '@/store/thunks/administratorsForm/createAdminThunk';
import { updateAdminThunk } from '@/store/thunks/administratorsForm/updateAdminThunk';
import type { FieldErrors } from '@/types/authState.types';
import { isFieldErrors } from '@/store/utils';

const initialValues: AdminUpsertDTO = { name: '', email: '', phone: '' };

const initialState: AdminFormState = {
  open: false,
  mode: 'create',
  values: initialValues,
  isSubmitting: false,
  fieldErrors: {},
};

/**
 * Redux slice for managing the account administrators form state.
 *
 * @remarks
 * Handles opening/closing the modal, switching between create/edit modes,
 * tracking form values, and managing API request lifecycle state.
 */
export const administratorsFormSlice = createSlice({
  name: 'administratorsForm',
  initialState,
  reducers: {
    openCreate(state, action: PayloadAction<Partial<AdminUpsertDTO> | undefined>) {
      state.open = true;
      state.mode = 'create';
      state.editingId = undefined;
      state.values = { ...initialValues, ...(action.payload ?? {}) };
      state.error = undefined;
      state.fieldErrors = {};
    },
    openEdit(state, action: PayloadAction<AdminEntity>) {
      const {
        payload: { id, name, email, phone },
      } = action;
      state.open = true;
      state.mode = 'edit';
      state.editingId = id;
      state.values = { name, email, phone };
      state.error = undefined;
      state.fieldErrors = {};
    },
    close(state) {
      state.open = false;
      state.isSubmitting = false;
      state.error = undefined;
      state.fieldErrors = {};
    },
    setValues(state, action: PayloadAction<Partial<AdminUpsertDTO>>) {
      state.values = { ...state.values, ...action.payload };
    },
    clearFieldErrors(state) {
      state.fieldErrors = {};
    },
  },
  extraReducers: (builder) => {
    // --- CREATE ---
    builder.addCase(createAdminThunk.pending, (state) => {
      state.isSubmitting = true;
      state.error = undefined;
      state.fieldErrors = {};
    });
    builder.addCase(createAdminThunk.fulfilled, (state) => {
      state.isSubmitting = false;
      state.open = false;
    });
    builder.addCase(createAdminThunk.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = 'error';

      let fieldErrors: FieldErrors = {};
      if (
        action.payload !== null &&
        typeof action.payload === 'object' &&
        'fieldErrors' in action.payload &&
        isFieldErrors(action.payload.fieldErrors)
      ) {
        const {
          payload: { fieldErrors: extractedFieldErrors },
        } = action;
        fieldErrors = extractedFieldErrors;
      }

      state.fieldErrors = fieldErrors;
    });

    // --- UPDATE ---
    builder.addCase(updateAdminThunk.pending, (state) => {
      state.isSubmitting = true;
      state.error = undefined;
      state.fieldErrors = {};
    });
    builder.addCase(updateAdminThunk.fulfilled, (state) => {
      state.isSubmitting = false;
      state.open = false;
    });
    builder.addCase(updateAdminThunk.rejected, (state, action) => {
      state.isSubmitting = false;
      state.error = 'error';

      let fieldErrors: FieldErrors = {};
      if (
        action.payload !== null &&
        typeof action.payload === 'object' &&
        'fieldErrors' in action.payload &&
        isFieldErrors(action.payload.fieldErrors)
      ) {
        const {
          payload: { fieldErrors: extractedFieldErrors },
        } = action;
        fieldErrors = extractedFieldErrors;
      }

      state.fieldErrors = fieldErrors;
    });
  },
});

const { actions } = administratorsFormSlice;
export const { openCreate, openEdit, close, setValues, clearFieldErrors } = actions;

export default administratorsFormSlice.reducer;
